import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { readReferral, clearReferral } from "@/lib/referralStash";

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const params = new URLSearchParams(hash.substring(1));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        navigate('/login');
        return;
      }

      try {
        // Phase 15.5 P5 — Referral persistence: pass any sessionStorage-stashed
        // referral code (saved by LoginPage/TrackPage/JoinPage BEFORE the
        // external Google redirect) so brand-new Google signups don't lose
        // their referral attribution.
        let pendingRef = null;
        pendingRef = readReferral();

        const params = { session_id: sessionId };
        if (pendingRef) params.referral_code = pendingRef;
        const response = await axios.post(`${API}/auth/google-session`, null, {
          params,
        });

        // Set cookie with proper attributes for production
        const isSecure = window.location.protocol === 'https:';
        const cookieString = `session_token=${response.data.session_token}; path=/; max-age=${7 * 24 * 60 * 60}; SameSite=${isSecure ? 'None; Secure' : 'Lax'}`;
        document.cookie = cookieString;
        console.log('Session cookie set:', cookieString);
        
        // Also store in localStorage as backup
        localStorage.setItem('session_token', response.data.session_token);

        // Defense-in-depth: if google-session didn't pick up the referral
        // (e.g. existing account on first ever sign-in), explicitly try to
        // apply it via the dedicated endpoint. Idempotent server-side.
        if (pendingRef) {
          try {
            await axios.post(`${API}/auth/apply-pending-referral`,
              { referral_code: pendingRef },
              { headers: { Authorization: `Bearer ${response.data.session_token}` } },
            );
          } catch (e) {
            console.warn('apply-pending-referral failed:', e?.response?.data || e?.message);
          }
          clearReferral();
        }
        
        // Phase 5: if a pending crew invite was stashed before login, redirect
        // straight to the crew so CrewDetailPage's invite-capture effect runs
        // and auto-joins the user.
        // Phase 15.5 P1: also honor an ?next= path stashed before the Google
        // OAuth redirect (e.g. logged-out producer hitting /submit-beat).
        let nextRoute = '/dashboard';
        try {
          const stash = sessionStorage.getItem('rgm_crew_invite');
          if (stash) {
            const parsed = JSON.parse(stash);
            if (parsed && parsed.crewId && parsed.code) {
              // Phase 15.5 P5 Batch E — preserve BOTH the invite code AND the
              // ref code through OAuth so the post-auth crew-join awards both
              // Crew Seeder credit AND personal referral credit.
              const qs = new URLSearchParams({ invite: parsed.code });
              if (parsed.ref) qs.set('ref', parsed.ref);
              nextRoute = `/crews/${parsed.crewId}?${qs.toString()}`;
            }
          }
        } catch { /* ignore */ }

        // Only override default with ?next= if no crew invite was pending
        if (nextRoute === '/dashboard') {
          try {
            const pendingNext = sessionStorage.getItem('rgm_oauth_next');
            if (pendingNext && pendingNext.startsWith('/') && !pendingNext.startsWith('//')) {
              nextRoute = pendingNext;
            }
          } catch { /* ignore */ }
        }
        // Clear the stash so it doesn't bleed into future logins
        try { sessionStorage.removeItem('rgm_oauth_next'); } catch { /* ignore */ }

        navigate(nextRoute, { replace: true, state: { user: response.data.user } });
      } catch (error) {
        console.error('Auth error:', error);
        navigate('/login');
      }
    };

    processSession();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#1a1a1e' }}>
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
        <p className="mt-4 text-gray-400">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;